<template>
  <div
    v-editable="blok"
    class="FeatureList"
    :class="desktopWidth('full')"
  >
    <div
      v-if="blok.headingTitle || blok.headingButtonText"
      class="flex flex-row flex-wrap justify-between items-center gap-8 mb-16 px-12 desk:px-0"
      :class="{'flex-col text-center' : blok.headingAlign === 'center'}"
    >
      <div class="type-headline-lg desk:type-headline-2xl"> {{ blok.headingTitle }}</div>

      <Nuxt-link
        :to="$sbLinkFix(blok.headingButtonLink)"
        :target="$sbLinkTarget(blok.headingButtonLink)"
      >
        <div
          v-if="blok.headingButtonText"
          class="btn btn--primary"
          :class="{
            'btn--secondary': blok.headingButtonStyle === 'secondary',
            'btn--ghost': blok.headingButtonStyle === 'ghost',
            'btn--text': blok.headingButtonStyle === 'plain',
          }"
        >
          {{ blok.headingButtonText }}
        </div>
      </Nuxt-link>
    </div>
    <div
      v-if="(blok.displaySlider && isMobile) || (featureCount > 3)"
      class="relative"
    >
      <client-only>
        <Swiper
          class="swiper-cover desk:!overflow-hidden !overflow-visible w-full desk:!pb-40 !pb-32"
          :modules="[SwiperNavigation, SwiperPagination, SwiperA11y]"
          :space-between="24"
          :pagination="{
            clickable: true,
            dynamicBullets: true,
          }"
          :navigation="{
            nextEl: `.swiper-button-next.swiper-for-blok-${blok._uid}`,
            prevEl: `.swiper-button-prev.swiper-for-blok-${blok._uid}`,
          }"

          :loop="false"
          :slides-per-view="1"
          :no-swiping="true"
          :breakpoints="{
            '1024': {
              slidesPerView: 3,
              spaceBetween: 12,
            }
          }"
        >
          <SwiperSlide
            v-for="(feature, index) in blok.content"
            :key="feature._uid"
            class="flex flex-col items-center mb-24 desk:mb-32 deskLarge:!overflow-hidden desk:cursor-grab desk:active:cursor-grabbing"
          >
            <div ref="imageContainer">
              <div
                v-if="feature.imageOrBackgroundVideo && feature.imageOrBackgroundVideo.filename"
                class="w-full -mb-40 desk:-mb-56"
                :class="{
                  'aspect-landscape' : blok.aspectRatio === 'aspect-landscape',
                  'aspect-square' : blok.aspectRatio === 'aspect-square',
                }"
              >
                <video
                  v-if="checkIfVideoFile(feature.imageOrBackgroundVideo.filename)"
                  width="w-full"
                  height="h-full"
                  class="w-full h-full object-cover"
                  loop
                  muted
                  autoplay
                  playsinline
                >
                  <source :src="feature.imageOrBackgroundVideo.filename" type="video/mp4">
                </video>
                <nuxt-img
                  v-else-if="feature.imageOrBackgroundVideo && feature.imageOrBackgroundVideo.filename"
                  preset="standard"
                  loading="lazy"
                  sizes="sm:100vw desk:1260px"
                  class="h-full w-full object-cover"
                  :src="feature.imageOrBackgroundVideo.filename"
                  :alt="feature.imageOrBackgroundVideo.alt"
                />
              </div>
            </div>
            <div
              v-if="feature.imageOrBackgroundVideo && feature.imageOrBackgroundVideo.filename"
              class="flex justify-center items-center relative z-10
                  w-40 h-40 desk:w-56 desk:h-56
                  bg-brandPrimary type-headline-xl desk:type-headline-3xl"
            >
              {{ index + 1 }}
            </div>
            <div
              v-else
              class="flex justify-center items-center
                  w-40 h-40 desk:w-56 desk:h-56 ml-12
                  bg-brandPrimary type-headline-xl desk:type-headline-3xl"
            >
              {{ index + 1 }}
            </div>
            <div class="px-12 pt-16 desk:pt-24">
              <div class="type-headline-base desk:type-headline-lg mb-4">{{ feature.headline }}</div>
              <div class="type-sm wysiwyg ">
                <ExtendedWysiwyg :content="feature.text" />
              </div>
            </div>
          </SwiperSlide>
        </Swiper>
      </client-only>
      <div class="hidden desk:block">
        <div
          class="swiper-button-prev"
          :class="{[`swiper-for-blok-${blok._uid}`]: true}"
        />
        <div
          class="swiper-button-next"
          :class="{[`swiper-for-blok-${blok._uid}`]: true}"
        />
      </div>
    </div>
    <div v-else class="flex flex-col desk:flex-row gap-32 desk:gap-12">
      <div
        v-for="(feature, index) in blok.content"
        :key="feature._uid"
        class="flex w-full flex-col"
      >
        <div ref="imageContainer">
          <div
            v-if="feature.imageOrBackgroundVideo && feature.imageOrBackgroundVideo.filename"
            class="w-full -mb-40 desk:-mb-56"
            :class="{
              'aspect-landscapre' : blok.aspectRatio === 'aspect-landscape',
              'aspect-square' : blok.aspectRatio === 'aspect-square',
            }"
          >
            <video
              v-if="checkIfVideoFile(feature.imageOrBackgroundVideo.filename)"
              width="w-full"
              height="h-full"
              class="w-full h-full object-cover"
              loop
              muted
              autoplay
              playsinline
            >
              <source :src="feature.imageOrBackgroundVideo.filename" type="video/mp4">
            </video>
            <nuxt-img
              v-else-if="feature.imageOrBackgroundVideo && feature.imageOrBackgroundVideo.filename"
              preset="standard"
              loading="lazy"
              sizes="sm:100vw desk:1260px"
              class="h-full w-full object-cover"
              :src="feature.imageOrBackgroundVideo.filename"
              :alt="feature.imageOrBackgroundVideo.alt"
            />
          </div>
        </div>
        <div
          v-if="feature.imageOrBackgroundVideo && feature.imageOrBackgroundVideo.filename"
          class="flex justify-center items-center
                  w-40 h-40 desk:w-56 desk:h-56
                  bg-brandPrimary type-headline-xl desk:type-headline-3xl"
        >
          {{ index + 1 }}
        </div>
        <div
          v-else
          class="flex justify-center items-center
                  w-40 h-40 desk:w-56 desk:h-56 ml-12
                  bg-brandPrimary type-headline-xl desk:type-headline-3xl"
        >
          {{ index + 1 }}
        </div>
        <div class="px-12 pt-16 desk:pt-24">
          <div class="type-headline-base desk:type-headline-lg mb-4">{{ feature.headline }}</div>
          <div class="type-sm wysiwyg">
            <ExtendedWysiwyg :content="feature.text" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { StoryBlokAsset, StoryBlokLink, StoryBlokPropBase } from '~/constants/types/storyblok';
import useDesktopWidth from '~/composeables/useDesktopWidth';
import { ISbRichtext } from 'storyblok-js-client';
import { checkIfVideoFile } from '~/util/imageUtil';

import { useUiStore } from '~/store/ui';
import { storeToRefs } from 'pinia';

const uiStore = useUiStore();
const { isMobile } = storeToRefs(uiStore);

type Content = StoryBlokPropBase & {
  headline: string;
  text: ISbRichtext;
  imageOrBackgroundVideo: StoryBlokAsset;
}

type Props = StoryBlokPropBase & {
  content: Content[];
  displaySlider: boolean;
  aspectRatio: 'aspect-landscape' | 'aspect-square',
  // Heading fields
  headingTitle: string;
  headingButtonText: string;
  headingButtonLink: StoryBlokLink;
  headingButtonStyle: 'primary' | 'secondary' | 'ghost' | 'plain';
  headingAlign: 'left' | 'center';
}

const props = defineProps<{
  blok: Props,
}>();

const { desktopWidth } = useDesktopWidth(props);

const imageContainer = ref<HTMLDivElement>();
const imageContainerHeight = ref(0);

onMounted(() => {
  imageContainerHeight.value = imageContainer.value && imageContainer.value.clientHeight || 0;
});

const featureCount = computed(() => props.blok.content.length);

</script>

<style lang="postcss">
.swiper-button-next {
  @apply !-right-16;
}

.swiper-button-prev {
  @apply !-left-16;
}
</style>
